/**
 * Route Guard for Application Pages
 * Prevents users from:
 * 1. Directly accessing application steps without completing previous ones
 * 2. Accessing application pages when multiple active applications exist (must select from dashboard first)
 */

import { useEffect, useState, useRef } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { useAppSelector } from "@/app/hooks";
import {
  selectApplicationData,
  selectIsReadOnly,
  selectHasMultipleActiveApplications,
  selectHasSelectedApplication,
} from "@/features/applicant/applicationSlice";
import {
  canAccessTab,
  getTabIndexFromPath,
  getCurrentTabFromLocation,
  TAB_PATHS,
  APPLICATION_TABS,
  getTabPathWithApplicantId,
  getTabNavigationOptions,
  type ApplicationTab,
} from "@lib/utils/applicationNavigation";
import { calculateRentAdded } from "@lib/utils/applicationHeaderHelpers";
import { showInfo } from "@lib/utils/notifications";

interface ApplicationRouteGuardProps {
  children: React.ReactNode;
}

export function ApplicationRouteGuard({
  children,
}: ApplicationRouteGuardProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { applicantId: urlApplicantId } = useParams<{ applicantId?: string }>();
  const applicationData = useAppSelector(selectApplicationData);
  const isReadOnly = useAppSelector(selectIsReadOnly);
  const hasMultipleActiveApplications = useAppSelector(
    selectHasMultipleActiveApplications,
  );
  const hasSelectedApplication = useAppSelector(selectHasSelectedApplication);
  const [isValidating, setIsValidating] = useState(true);
  const previousPathRef = useRef<string>(location.pathname);
  const previousTabRef = useRef<ApplicationTab | null>(null);

  // Get applicant ID from URL or Redux
  const applicantId = urlApplicantId || applicationData?.applicant_id;

  // Check if payment is added (allows direct navigation if true)
  const isPaymentAdded = calculateRentAdded(applicationData);

  useEffect(() => {
    // Get current tab from location state
    const locationState = location.state as { tab?: ApplicationTab } | null;
    const currentTab = getCurrentTabFromLocation(location);
    const currentTabIndex = getTabIndexFromPath(
      location.pathname,
      locationState,
      location.search,
    );

    // Track previous tab to detect changes even if pathname remains same
    const tabChanged = previousTabRef.current !== currentTab;
    previousTabRef.current = currentTab;

    // If not an application page, allow access
    if (currentTabIndex === -1) {
      setIsValidating(false);
      previousPathRef.current = location.pathname;
      return;
    }

    // CHECK 1: If multiple active applications exist and user hasn't selected one,
    // redirect to dashboard. Skip this check when an applicantId is present in the URL
    // because ResidentApplication handles the Angular-style checkActiveApplications()
    // logic (only redirects if this applicantId is among the active apps, allowing
    // non-active apps like EXPIRED to be viewed directly).
    if (hasMultipleActiveApplications && !hasSelectedApplication && !applicantId) {
      showInfo(
        "We have found multiple active invites and applications, please select one of them to proceed.",
        0,
      );
      navigate("/applicant/dashboard", { replace: true });
      setIsValidating(false);
      return;
    }

    // CRITICAL FIX: Only check access and redirect if the pathname or tab actually changed
    // This prevents redirecting when applicationData updates (e.g., after save)
    // but the user is still on the same page/tab
    const pathChanged = previousPathRef.current !== location.pathname;
    previousPathRef.current = location.pathname;

    // If neither path nor tab changed, user is still on the same step - allow them to stay
    // This allows users to save data without being redirected
    if (!pathChanged && !tabChanged) {
      setIsValidating(false);
      return;
    }

    // STRICT STEP-WISE NAVIGATION: Check if current tab can be accessed
    // This prevents users from directly accessing URLs or navigating to tabs without completing previous steps
    const accessCheck = canAccessTab(
      currentTabIndex,
      applicationData,
      isReadOnly,
      isPaymentAdded,
      true,
    );

    if (!accessCheck.canAccess) {
      // Redirect to first incomplete tab
      const firstIncompleteTab = accessCheck.firstIncompleteTab ?? 0;
      const redirectTab = APPLICATION_TABS[firstIncompleteTab];

      // Use resident-application route if applicant ID is available, otherwise use old route
      const redirectPath = applicantId
        ? getTabPathWithApplicantId(redirectTab, applicantId)
        : TAB_PATHS[redirectTab];

      // Only redirect if we're not already on the redirect tab (path with query or state)
      const currentTab = getCurrentTabFromLocation(location);
      const alreadyOnRedirectTab = applicantId
        ? currentTab === redirectTab
        : location.pathname === redirectPath;
      if (!alreadyOnRedirectTab) {
        if (applicantId) {
          // Use location state for new route format
          navigate(redirectPath, {
            ...getTabNavigationOptions(redirectTab),
            replace: true,
          });
        } else {
          // Use regular navigation for old route format
          navigate(redirectPath, { replace: true });
        }
      }
    }

    setIsValidating(false);
  }, [
    location.pathname,
    location.search,
    location.state,
    applicationData,
    isReadOnly,
    isPaymentAdded,
    navigate,
    applicantId,
    hasMultipleActiveApplications,
    hasSelectedApplication,
  ]);

  // Show nothing while validating to prevent flash of unauthorized content
  if (isValidating) {
    return null;
  }

  return <>{children}</>;
}
